import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { X, Users, User } from 'lucide-react';

interface EventAttendee {
  user_id: string;
  username: string;
  avatar_url?: string;
  response_type: string;
  created_at: string;
}

interface EventAttendeesModalProps {
  eventId: string;
  eventTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function EventAttendeesModal({ 
  eventId, 
  eventTitle, 
  isOpen, 
  onClose 
}: EventAttendeesModalProps) {
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && eventId) {
      fetchAttendees();
    }
  }, [isOpen, eventId]);

  async function fetchAttendees() {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('get_event_attendees', {
        event_uuid: eventId
      });

      if (error) throw error;

      setAttendees(data || []);
    } catch (err) {
      console.error('Error fetching attendees:', err);
      setError(err instanceof Error ? err.message : 'Failed to load attendees');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--color-card)',
        borderRadius: '12px',
        padding: '1.5rem',
        maxWidth: '400px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        border: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Users size={20} color="var(--color-accent)" />
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
              Event Attendees
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Event Title */}
        <div style={{
          fontSize: '0.875rem',
          color: '#999',
          marginBottom: '1rem',
          padding: '0.5rem',
          backgroundColor: 'rgba(196, 255, 0, 0.1)',
          borderRadius: '6px',
          border: '1px solid rgba(196, 255, 0, 0.2)'
        }}>
          <strong style={{ color: 'var(--color-accent)' }}>Event:</strong> {eventTitle}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
              color: '#999'
            }}>
              <div className="spinner" style={{ marginRight: '0.5rem' }}></div>
              Loading attendees...
            </div>
          )}

          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: 'rgba(228, 87, 77, 0.1)',
              border: '1px solid rgba(228, 87, 77, 0.3)',
              borderRadius: '6px',
              color: 'var(--color-danger)',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          {!loading && !error && attendees.length === 0 && (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#999'
            }}>
              <Users size={48} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No attendees yet</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
                Be the first to join this event!
              </p>
            </div>
          )}

          {!loading && !error && attendees.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {attendees.map((attendee) => (
                <div
                  key={attendee.user_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: 'var(--color-background)',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)'
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    backgroundColor: '#1f1f1f',
                    border: '1px solid var(--color-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    {attendee.avatar_url ? (
                      <img
                        src={attendee.avatar_url}
                        alt={attendee.username}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <User size={20} color="#666" />
                    )}
                  </div>

                  {/* User Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      color: 'var(--color-text)',
                      marginBottom: '0.25rem'
                    }}>
                      {attendee.username}
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#999'
                    }}>
                      Joined {new Date(attendee.created_at).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: 'rgba(196, 255, 0, 0.1)',
                    border: '1px solid rgba(196, 255, 0, 0.3)',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    color: 'var(--color-accent)',
                    fontWeight: '500',
                    textTransform: 'capitalize'
                  }}>
                    {attendee.response_type}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '1rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid var(--color-border)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: '#999'
          }}>
            {attendees.length} {attendees.length === 1 ? 'person' : 'people'} attending
          </div>
        </div>
      </div>
    </div>
  );
}
